import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import {
  getExpiresAt,
  getWillRefresh,
  getNextRefresh,
  isTimedOut,
  isLoggedOut,
  mergeToSession,
  setSessionChannel,
  updateSession,
} from '@xmatters/redux/services/sessionService';

import { hideReloginDialog, showReloginDialog } from 'actions/reloginDialog';
import {
  showSessionTimeoutDialog,
  hideSessionTimeoutDialog,
  restartSession as restartSessionAction,
  setSessionUserId,
} from '@xmatters/redux/actions/session';
import {
  featureToggleRequestStatus,
  isConcurrentSessionsDeniedEnabled,
} from 'selectors/featureToggles';
import { getLoggedInUserId } from 'selectors/loggedInUser';
import { isReloginDialogOpen } from 'selectors/reloginDialog';
import {
  getMaxSessionDuration,
  isReportingPagesExcluded,
  isWarningDialogOpen,
  getSessionUserId,
  isUsingSSO,
} from 'selectors/session';
import { getTokenPromise } from '@xmatters/xm-api/xmApiToken';
import {
  XMATTERS_SIGN_OUT_URL,
  XMATTERS_SIGN_ON_URL,
  XMATTERS_SIGN_OUT_URL_AUTO_TIMEOUT,
} from 'xm-constants/urls';

const EVENT_HANDLERS_TYPES = ['click', 'keydown', 'scroll'];
export const SESSION_TIMEOUT_EXCLUDED_PAGE_FRAGMENTS = [
  'EventsReportPlace',
  'Alerts',
  'CompanyNotificationReportPlace',
  'ConferencesReportPlace',
  'IntegrationLogPlace',
  'DashboardPlace',
];

const EXTEND_SESSION_DEBOUNCE_DURATION = 500;
const SESSION_CHECK_INTERVAL_IN_MS = 500;
const SESSION_EXPIRED_CHECK_INTERVAL_IN_MS = 15000;
// set to 60 as per COR-1853
const WARNING_BUFFER_IN_S = 60;

let isEventAttached = false;
let isLeader = false;
let store;
let elector;

/**
 * Add maxSessionDuration to nextRefresh time
 * @param {number} maxSessionDuration
 */
export const resetNextRefreshTime = maxSessionDuration => {
  updateSession({
    willRefresh: false,
    nextRefresh: moment().add(maxSessionDuration, 'minutes').format(),
  });
};

export const refreshCas = () => {
  window.ebfd.refreshCas();
};

/**
 * Add maxSessionDuration to expiresAt time
 * @param {number} maxSessionDuration
 */
export const extendSession = maxSessionDuration => {
  const expiresAt = moment().add(maxSessionDuration, 'minutes').format();
  updateSession({
    willRefresh: true,
    expiresAt,
    timedOut: false,
  });
};

/**
 *.
 * @param {object} location
 * @returns true if window location is currently on a report page
 */
export const isCurrentlyOnReport = location => {
  const { hash, pathname } = location;
  return SESSION_TIMEOUT_EXCLUDED_PAGE_FRAGMENTS.some(
    page => pathname?.includes(page) || hash?.includes(page),
  );
};

/**
 * Show warning modal
 */
export const showWarning = () => {
  store?.dispatch(
    showSessionTimeoutDialog(getExpiresAt().diff(moment(), 'seconds')),
  );
};

/**
 * Hide warning modal
 */
export const hideWarning = () => {
  store?.dispatch(hideSessionTimeoutDialog());
};

/**
 * Show relogin modal
 */

export const showRelogin = () => {
  store?.dispatch(showReloginDialog());
};

/**
 * Hide relogin modal
 */

export const hideRelogin = () => {
  store?.dispatch(hideReloginDialog());
};

/**
 *
 * @returns true if session has expired, false otherwise
 */
export const isSessionExpired = () => {
  const expiresAt = getExpiresAt();
  return expiresAt.isValid() && moment().isSameOrAfter(expiresAt);
};

/**
 *
 * @param {boolean} reportingPagesExcluded
 * @returns true if within next refresh time
 */
export const shouldRefreshToken = reportingPagesExcluded => {
  return (
    ((reportingPagesExcluded && isCurrentlyOnReport(window.location)) ||
      getWillRefresh()) &&
    moment().isBetween(
      getNextRefresh()?.clone()?.subtract(10, 'seconds'),
      getNextRefresh(),
    )
  );
};

/**
 * Determine whether warning dialog should be open
 * @returns true if expiry time is within buffer
 */
export const shouldOpenWarningDialog = () => {
  return moment().isBetween(
    getExpiresAt()?.clone()?.subtract(WARNING_BUFFER_IN_S, 'seconds'),
    getExpiresAt(),
  );
};

/**
 * End session
 */
export const endSession = () => {
  if (!isTimedOut(store.getState())) {
    window.XM_listenForGwtBaseUrlData?.();
  }
  if (!isTimedOut(store.getState()) && isLeader) {
    updateSession({ timedOut: true });
    fetch(XMATTERS_SIGN_OUT_URL_AUTO_TIMEOUT)
      .then(() => {
        // block on all tabs then signout on one tab
        showRelogin();
      })
      .catch((...args) => {
        // could not fetch XMATTERS_SIGN_OUT_URL_AUTO_TIMEOUT...
        // Currently not doing anything to handle this, but should we do something specific here?
        // E.g. updateSession({ timedOut: false }); so that it automatically tries again later?

        // eslint-disable-next-line no-console
        console.error('Failed to load xmatters signout.', args);
      });
  } else if (isTimedOut(store.getState()) && isSessionExpired()) {
    showRelogin();
  }
};
/**
 * Logout
 */
export const logout = () => {
  fetch(XMATTERS_SIGN_OUT_URL).then(() => {
    updateSession({
      loggedOut: true,
    });
  });
};

/**
 * Event handler for EVENT_HANDLERS_TYPES that will extend the session
 */
const handleEvent = () => {
  if (!shouldOpenWarningDialog() && !isSessionExpired()) {
    extendSession(getMaxSessionDuration(store.getState()));
  }
};

/**
 * Event handler for blur of Iframes
 */
const getBlurEventHandler = element => {
  function handleBlurEvent() {
    EVENT_HANDLERS_TYPES.forEach(type => {
      element.contentWindow?.removeEventListener(type, handleEvent);
    });
    element.contentWindow?.removeEventListener('blur', handleBlurEvent);
    isEventAttached = false;
  }
  return handleBlurEvent;
};

/**
 * Check if session has expired, needs a refresh, should trigger warning
 * @param {number} maxSessionDuration
 * @param {boolean} isReportingPagesExcluded
 * @returns {Promise}
 */
export const checkSession = (maxSessionDuration, reportingPagesExcluded) => {
  if (
    getLoggedInUserId(store.getState()) !== getSessionUserId(store.getState())
  ) {
    // if relogged in as a different user, force reload
    window.location.reload();
  } else if (isLoggedOut(store.getState())) {
    window.location.href = isUsingSSO(store.getState())
      ? XMATTERS_SIGN_OUT_URL
      : XMATTERS_SIGN_ON_URL;
  } else if (shouldRefreshToken(reportingPagesExcluded)) {
    getTokenPromise({ force: true }).then(() => {
      resetNextRefreshTime(maxSessionDuration);
    });
  } else if (reportingPagesExcluded && isCurrentlyOnReport(window.location)) {
    extendSession(maxSessionDuration);
  } else if (isSessionExpired()) {
    endSession();
  } else if (shouldOpenWarningDialog()) {
    showWarning();
  }

  if (isWarningDialogOpen(store.getState()) && !shouldOpenWarningDialog()) {
    hideWarning();
  }

  if (isReloginDialogOpen(store.getState()) && !isSessionExpired()) {
    hideRelogin();
  }

  const element = document.activeElement;
  if (
    element.tagName === 'IFRAME' &&
    !isEventAttached &&
    !!element.contentWindow &&
    new URL(element.src).origin === window.location.origin
  ) {
    EVENT_HANDLERS_TYPES.forEach(type => {
      element.contentWindow.addEventListener(type, handleEvent, true);
    });
    element.contentWindow.addEventListener(
      'blur',
      getBlurEventHandler(element),
    );
    isEventAttached = true;
  }
};

/**
 * Setup session service listeners, checks, choose leader tab
 */
export const setupSessionService = () => {
  if (!store) {
    return;
  }

  const xmSessionChannel = new BroadcastChannel('xmSessionChannel');
  setSessionChannel(xmSessionChannel);

  xmSessionChannel.addEventListener('message', payload => {
    mergeToSession(payload);
  });

  // select a tab that will be leader.  this leader tab will be the only tab
  // that will sign out upon session expiry
  elector = createLeaderElection(xmSessionChannel, {
    fallbackInterval: 2000,
    responseTime: 1000,
  });
  elector.awaitLeadership().then(() => {
    isLeader = true;
  });

  updateSession({
    expiresAt: moment()
      .add(getMaxSessionDuration(store.getState()), 'minutes')
      .format(),
    nextRefresh: moment()
      .add(getMaxSessionDuration(store.getState()), 'minutes')
      .format(),
    timedOut: false,
  });

  // Every SESSION_EXPIRED_CHECK_INTERVAL_IN_MS, fetch a new token so
  // that if there is another session active, it will log the user out
  // only in cases where the concurrent sessions denied CC is enabled
  if (isConcurrentSessionsDeniedEnabled(store.getState())) {
    setInterval(
      () =>
        getTokenPromise({
          force: true,
          onError: error => {
            if (error.code === 401) {
              window.location.href = isUsingSSO(store.getState())
                ? XMATTERS_SIGN_OUT_URL
                : XMATTERS_SIGN_ON_URL;
            }
          },
        }),
      SESSION_EXPIRED_CHECK_INTERVAL_IN_MS,
    );
  }

  setInterval(() => {
    checkSession(
      getMaxSessionDuration(store.getState()),
      isReportingPagesExcluded(store.getState()),
    );
  }, SESSION_CHECK_INTERVAL_IN_MS);

  EVENT_HANDLERS_TYPES.forEach(type => {
    window.addEventListener(
      type,
      debounce(handleEvent, EXTEND_SESSION_DEBOUNCE_DURATION),
      true,
    );
  });
};

/**
 * Check if app has loaded feature toggles, logged in user information and
 * security settings which are all required to load session service
 * @returns {boolean} return true if app has loaded all it needs
 */
const isAppReady = () =>
  getLoggedInUserId(store.getState()) &&
  featureToggleRequestStatus(store.getState()) === 'LOADED' &&
  getMaxSessionDuration(store.getState()) !== null &&
  isReportingPagesExcluded(store.getState()) !== null;

/**
 * Pass redux store into sessionService and call setupSessionService only
 * @param {object} reduxStore
 */
export const initializeSessionService = reduxStore => {
  store = reduxStore;
  const unsubscribe = store.subscribe(() => {
    if (isAppReady()) {
      setupSessionService();
      unsubscribe();
      store.dispatch(setSessionUserId(getLoggedInUserId(store.getState())));
    }
  });
};

/**
 * Restart the session polling
 */
export const restartSession = () => {
  store.dispatch(restartSessionAction());
  getTokenPromise({ force: true }).then(() => {
    resetNextRefreshTime(getMaxSessionDuration(store.getState()));
  });
  extendSession(getMaxSessionDuration(store.getState()));
};
